/**
 * Movement paperwork.
 *
 * A scan records that something *has* happened. A transfer says something is
 * *going to* happen, and carries the gate pass that lets the tool off site. The
 * two meet on their own: the scan pipeline already attaches a scan to whatever
 * transfer is open for that mould, so the ledger fills the paperwork in as the
 * tool passes each checkpoint.
 *
 * Online-only, like People. A transfer is a live claim about something moving
 * right now; a stale local copy of one is worse than none.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, FileText, Plus, Search, Send, Truck, X } from 'lucide-react';
import {
  ApiClientError,
  type CreateTransferInput,
  type TransferDetail,
  type TransferRow,
  type TransferStatus,
} from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Dialog, ConfirmDialog } from '../../components/ui/dialog.js';
import { Field, Input, Select } from '../../components/ui/field.js';
import { Menu, MenuItem } from '../../components/ui/menu.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import {
  createTransfer,
  isNetworkError,
  listTransfers,
  updateTransfer,
} from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { useMasterOptions, useMoulds } from '../../db/queries.js';
import { GatePass } from './GatePass.js';

const STATUS_TONE: Record<TransferStatus, 'neutral' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  IN_TRANSIT: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const STATUS_LABEL: Record<TransferStatus, string> = {
  DRAFT: 'Draft',
  IN_TRANSIT: 'In transit',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export function TransfersPage() {
  const canWrite = useCan('create', 'transfers');
  const [rows, setRows] = useState<TransferRow[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [printing, setPrinting] = useState<TransferDetail | null>(null);
  const [confirm, setConfirm] = useState<{ row: TransferRow; action: 'cancel' | 'complete' } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const locations = useMasterOptions('locations');
  const locationName = useMemo(
    () => new Map((locations ?? []).map((l) => [l.value, l.label])),
    [locations],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await listTransfers());
    } catch (e) {
      setRows([]);
      setError(
        isNetworkError(e)
          ? 'No connection. Transfers can only be managed online.'
          : 'Could not load transfers.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (!needle) return true;
      return `${r.transferNo} ${r.mould?.assetCode ?? ''} ${r.mould?.name ?? ''}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, search, status]);

  async function act(row: TransferRow, action: 'dispatch' | 'cancel' | 'complete') {
    setBusy(true);
    setError(null);
    try {
      // `complete` is a manual override on the server and it insists on a
      // reason, because the normal way a transfer completes is a scan at the
      // far end - closing one by hand means the scan did not happen.
      await updateTransfer(row.id, {
        action,
        ...(action === 'complete' ? { remarks: 'Closed manually from the transfers screen' } : {}),
      });
      await load();
      setConfirm(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'That action could not be applied.');
    } finally {
      setBusy(false);
    }
  }

  const columns: Array<Column<TransferRow>> = [
    {
      key: 'transferNo',
      header: 'Transfer',
      primary: true,
      sortValue: (r) => r.transferNo,
      render: (r) => <Code className="font-semibold">{r.transferNo}</Code>,
    },
    {
      key: 'mould',
      header: 'Mould',
      sortValue: (r) => r.mould?.assetCode ?? '',
      render: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <Code>{r.mould?.assetCode ?? '-'}</Code>
          <span className="truncate text-muted-foreground">{r.mould?.name}</span>
        </span>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      hideOnMobile: true,
      render: (r) => (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          {r.fromLocation?.code ?? '-'}
          <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden />
          {r.toLocation?.code ?? '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      render: (r) => <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>,
    },
    {
      key: 'expected',
      header: 'Expected',
      hideOnMobile: true,
      sortValue: (r) => r.expectedArrivalAt ?? '',
      render: (r) => {
        if (!r.expectedArrivalAt) return <span className="text-subtle-foreground">-</span>;
        const late =
          r.status === 'IN_TRANSIT' && new Date(r.expectedArrivalAt).getTime() < Date.now();
        return (
          <span className={late ? 'text-danger' : undefined}>
            {new Date(r.expectedArrivalAt).toLocaleDateString()}
            {late ? ' · overdue' : ''}
          </span>
        );
      },
    },
  ];

  return (
    <Page
      title="Transfers"
      description="Moving a mould between sites, with the gate pass that goes with it."
      actions={
        canWrite ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New transfer
          </Button>
        ) : null
      }
      toolbar={
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by transfer number or mould…"
              className="pl-9"
              type="search"
              aria-label="Search transfers"
            />
          </div>
          <Select
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Any status"
            options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
            className="sm:w-48"
          />
        </div>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.id}
        initialSort={{ key: 'transferNo', direction: 'desc' }}
        rowActions={(r) => (
          <Menu
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Actions for ${r.transferNo}`}>
                <FileText className="size-4" />
              </Button>
            }
          >
            <MenuItem
              icon={<FileText />}
              onSelect={() =>
                setPrinting({
                  ...(r as TransferRow & TransferDetail),
                  expectedRoute: [],
                } as TransferDetail)
              }
            >
              Print gate pass
            </MenuItem>
            {canWrite && r.status === 'DRAFT' ? (
              <MenuItem icon={<Send />} onSelect={() => void act(r, 'dispatch')}>
                Dispatch
              </MenuItem>
            ) : null}
            {canWrite && r.status === 'IN_TRANSIT' ? (
              <MenuItem icon={<Truck />} onSelect={() => setConfirm({ row: r, action: 'complete' })}>
                Mark arrived
              </MenuItem>
            ) : null}
            {canWrite && (r.status === 'DRAFT' || r.status === 'IN_TRANSIT') ? (
              <MenuItem destructive icon={<X />} onSelect={() => setConfirm({ row: r, action: 'cancel' })}>
                Cancel
              </MenuItem>
            ) : null}
          </Menu>
        )}
        empty={
          <EmptyState
            icon={<Truck />}
            title={rows?.length ? 'Nothing matches' : 'No transfers yet'}
            description={
              rows?.length
                ? 'Try a different search, or clear the status filter.'
                : 'Raise one when a mould needs to leave the site. The gate pass prints from here.'
            }
            action={
              canWrite && !rows?.length ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  <Plus className="size-4" /> New transfer
                </Button>
              ) : null
            }
          />
        }
      />

      {creating ? (
        <NewTransferDialog
          open
          onOpenChange={setCreating}
          onSaved={() => void load()}
        />
      ) : null}

      {printing ? (
        <GatePass
          transfer={printing}
          fromLabel={locationName.get(printing.fromLocationId) ?? printing.fromLocation?.code ?? '-'}
          toLabel={locationName.get(printing.toLocationId) ?? printing.toLocation?.code ?? '-'}
          companyName="MouldTrack"
          onDone={() => setPrinting(null)}
        />
      ) : null}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(open) => !open && setConfirm(null)}
        destructive={confirm?.action === 'cancel'}
        loading={busy}
        confirmLabel={confirm?.action === 'cancel' ? 'Cancel transfer' : 'Mark arrived'}
        onConfirm={() => confirm && void act(confirm.row, confirm.action)}
        title={
          confirm?.action === 'cancel'
            ? `Cancel ${confirm.row.transferNo}?`
            : `Mark ${confirm?.row.transferNo} as arrived?`
        }
        description={
          confirm?.action === 'cancel'
            ? 'The mould stays where it is. Its scan history is untouched.'
            : 'Normally a scan at the far end closes a transfer. Closing it by hand records that the arrival scan did not happen.'
        }
      />
    </Page>
  );
}

/** Raise a transfer: which mould, from where, to where, expected when. */
function NewTransferDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const moulds = useMoulds({});
  const locations = useMasterOptions('locations');
  const stores = useMasterOptions('stores');

  const [mouldId, setMouldId] = useState('');
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [toStoreId, setToStoreId] = useState('');
  const [expected, setExpected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mouldOptions = useMemo(
    () =>
      (moulds ?? [])
        .filter((m) => m.assetCode)
        .map((m) => ({
          value: m.id,
          label: m.name && m.name !== m.assetCode ? `${m.assetCode} - ${m.name}` : m.assetCode,
        })),
    [moulds],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!mouldId || !fromLocationId || !toLocationId) {
      setError('Choose a mould and both locations.');
      return;
    }
    if (fromLocationId === toLocationId) {
      setError('A transfer has to go somewhere else - the two locations are the same.');
      return;
    }
    setSaving(true);
    try {
      const input: CreateTransferInput = {
        mouldId,
        fromLocationId,
        toLocationId,
        toStoreId: toStoreId || null,
        expectedRoute: [],
        // A date input gives a day; the API wants an instant. End of that day is
        // the honest reading of "expected back by the 26th".
        expectedArrivalAt: expected ? new Date(`${expected}T23:59:59`).toISOString() : null,
      };
      await createTransfer(input);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : isNetworkError(err)
            ? 'No connection. Transfers can only be raised online.'
            : 'Could not raise the transfer.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="New transfer"
      description="Raises the paperwork. Print the gate pass, then dispatch when it leaves."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="transfer-form" loading={saving}>
            Raise transfer
          </Button>
        </>
      }
    >
      <form id="transfer-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Mould" required>
          <Select
            value={mouldId}
            onChange={(e) => setMouldId(e.target.value)}
            placeholder="Choose a mould"
            options={mouldOptions}
          />
        </Field>

        <Field label="From" required>
          <Select
            value={fromLocationId}
            onChange={(e) => setFromLocationId(e.target.value)}
            placeholder="Where it is now"
            options={locations ?? []}
          />
        </Field>

        <Field label="To" required>
          <Select
            value={toLocationId}
            onChange={(e) => setToLocationId(e.target.value)}
            placeholder="Where it is going"
            options={locations ?? []}
          />
        </Field>

        <Field label="Into store" hint="Optional - a specific rack or room at the far end.">
          <Select
            value={toStoreId}
            onChange={(e) => setToStoreId(e.target.value)}
            placeholder="Not specified"
            options={stores ?? []}
          />
        </Field>

        <Field label="Expected arrival" hint="Drives the overdue flag on this screen.">
          <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
        </Field>

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
